import React, { useEffect } from 'react';

interface FaviconSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: number;
  version?: string;
}

/**
 * FaviconSpinner
 * Reusable spinner that uses the premium favicon animation.
 * Works inside Ant Design loaders, tables, modals, etc.
 */
let keyframesInjected = false;

const injectKeyframes = () => {
  if (keyframesInjected || typeof document === 'undefined') {
    return;
  }

  const style = document.createElement('style');
  style.id = 'fromas-favicon-keyframes';
  style.innerHTML = `
    @keyframes fromas-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @keyframes fromas-glow {
      0%, 100% {
        filter: drop-shadow(0 0 3px rgba(93, 183, 232, 0.4)) drop-shadow(0 0 8px rgba(93, 183, 232, 0.2));
      }
      50% {
        filter: drop-shadow(0 0 6px rgba(93, 183, 232, 0.5)) drop-shadow(0 0 12px rgba(93, 183, 232, 0.3));
      }
    }

    @keyframes fromas-spark {
      0%, 100% {
        transform: scale(0.9);
        opacity: 0.25;
      }
      50% {
        transform: scale(1.1);
        opacity: 0.6;
      }
    }

  `;

  document.head.appendChild(style);
  keyframesInjected = true;
};

const FaviconSpinner: React.FC<FaviconSpinnerProps> = ({
  size = 36,
  version = '5',
  style,
  ...rest
}) => {
  useEffect(() => {
    injectKeyframes();
  }, []);

  const animationStyle = 'fromas-spin 1.8s linear infinite, fromas-glow 3s ease-in-out infinite';

  const innerSize = size + 18;

  return (
    <span
      role="img"
      aria-label="Fromas Cloud loading indicator"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: innerSize,
        height: innerSize,
        minWidth: innerSize,
        minHeight: innerSize,
        pointerEvents: 'none',
        ...style,
      }}
      {...rest}
    >
      <span
        style={{
          position: 'absolute',
          width: size + 12,
          height: size + 12,
          borderRadius: '50%',
          border: '1px solid rgba(93, 183, 232, 0.28)',
          boxShadow: '0 0 12px rgba(93, 183, 232, 0.2)',
          animation: 'fromas-spark 2.4s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <img
        src={`/favicon.ico?v=${version}`}
        alt="Fromas Loader"
        className="fromas-loader"
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          display: 'block',
          animation: animationStyle,
        }}
        onError={(e) => {
          console.error('FaviconSpinner: favicon missing', e);
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
    </span>
  );
};

export default FaviconSpinner;

